import { createSelector } from '@reduxjs/toolkit';
import { getRouteID as getRouteIDKey } from 'lib/utils/getActiveRoute';
// store selectors
const getStoreExplorerStore = state => state.genericSE;
const getLayoutStore = state => state.layoutStore;

const getRouteID = () => {
  const {
    location: { pathname }
  } = window;
  return getRouteIDKey(pathname);
};

const selectLayout = createSelector([getLayoutStore], ({ routes: list }) => {
  const routeName =
    getRouteID() === 'redirect' ? 'store-explorer-v2' : getRouteID();
  return list.layout.length > 0
    ? list.layout.filter(l => l.routeID === routeName)[0]
    : [];
});

const getLayout = createSelector([getLayoutStore], ({ routes: list }) => {
  return list.layout;
});

const selectApiRequestStatus = createSelector(
  [getStoreExplorerStore],
  ({ apiRequestStatus }) => apiRequestStatus
);

const getIsFetchingFilter = createSelector(
  getStoreExplorerStore,
  ({ apiRequestStatus }) => apiRequestStatus.filter.request
);

const selectFilters = createSelector(
  getStoreExplorerStore,
  ({ filters }) => filters
);

const getCurrentFilter = createSelector(
  getStoreExplorerStore,
  ({ filter }) => filter
);

const getBottomUpFilter = createSelector(
  getStoreExplorerStore,
  ({ bottomUpFilter }) => bottomUpFilter
);

const getFilterEpoch = createSelector(
  getStoreExplorerStore,
  ({ filterEpoch }) => filterEpoch
);

const isFetchingFilter = createSelector(
  getStoreExplorerStore,
  ({ apiRequestStatus }) =>
    apiRequestStatus.bottomUpFilter.request || apiRequestStatus.filter.request
);

const selectSessionData = createSelector(
  getStoreExplorerStore,
  ({ sessionData }) => sessionData
);

// --- session data, assembled from the three parallel legs -------------------

// Build-time flag; absent from every env file but .env.development.local, so
// the shipped default is to wait for all three legs.
export const isProgressiveRenderEnabled =
  process.env.REACT_APP_SHELF_PROGRESSIVE_RENDER === 'true';

const selectSplitSessionDataStatus = createSelector(
  getStoreExplorerStore,
  ({ apiRequestStatus }) => apiRequestStatus.splitSessionData
);

const isFetchingSplitSessionData = createSelector(
  [selectSplitSessionDataStatus],
  ({ images, misc, variants }) =>
    images.request || misc.request || variants.request
);

// Any leg failing fails the whole page. `failedLeg` names which one so the
// message can be specific without exposing the server's error text.
const selectSplitSessionDataFailedLeg = createSelector(
  [selectSplitSessionDataStatus],
  ({ failedLeg }) => failedLeg
);

/**
 * Whether the assembled sessionData is complete enough to hand to
 * ShelfVisualizer. In progressive mode images+misc are enough - the reducer
 * guarantees `variants: []` until the variants leg lands, which the visualiser
 * tolerates (it shows its own empty-table state and self-corrects on arrival).
 */
const isSplitSessionDataReady = createSelector(
  [selectSplitSessionDataStatus],
  ({ images, misc, variants }) =>
    isProgressiveRenderEnabled
      ? images.success && misc.success
      : images.success && misc.success && variants.success
);

const isSplitSessionDataComplete = createSelector(
  [selectSplitSessionDataStatus],
  ({ images, misc, variants }) =>
    images.success && misc.success && variants.success
);

const selectChartData = createSelector(
  getStoreExplorerStore,
  ({ chartData }) => chartData
);

const selectChartList = createSelector(
  getStoreExplorerStore,
  ({ chartList }) => chartList
);

const selectReportData = createSelector(
  [getStoreExplorerStore],
  ({ reportData }) => reportData
);
// to fetch the filters
const selectSessionFilters = createSelector(
  [getStoreExplorerStore],
  ({ sessionFilters }) => sessionFilters
);

const selectChartFetched = createSelector(
  [getStoreExplorerStore],
  ({ apiRequestStatus }) => apiRequestStatus.chartData.fetchedChart
);

const selectChartListFetching = createSelector(
  [getStoreExplorerStore],
  ({ apiRequestStatus }) => apiRequestStatus.chartList.request
);

const getGranularitiesList = (state, props) =>
  state.genericSE.preferences.granularities[props.routeID];

const getFilterPreferences = (state, props) =>
  state.genericSE.preferences.filters[props.routeID];

const makeGetParamsValueList = () =>
  createSelector(
    [getFilterPreferences, getGranularitiesList],
    (filterPrefs, grPrefs) => {
      return {
        ...filterPrefs,
        ...grPrefs
      };
    }
  );

const selectFilterValues = createSelector(
  getStoreExplorerStore,
  ({ sessionFilterValues }) => sessionFilterValues
);

const sessionFilterValuesStatus = createSelector(
  getStoreExplorerStore,
  ({ apiRequestStatus }) => apiRequestStatus.sessionFilterValuesStatus
);

export {
  getRouteID,
  selectApiRequestStatus,
  selectChartData,
  selectChartList,
  selectFilters,
  selectLayout,
  selectSessionData,
  selectReportData,
  selectSessionFilters,
  selectFilterValues,
  selectChartFetched,
  makeGetParamsValueList,
  sessionFilterValuesStatus,
  getCurrentFilter,
  getIsFetchingFilter,
  getLayout,
  selectChartListFetching,
  getBottomUpFilter,
  isFetchingFilter,
  getFilterEpoch,
  selectSplitSessionDataStatus,
  isFetchingSplitSessionData,
  selectSplitSessionDataFailedLeg,
  isSplitSessionDataReady,
  isSplitSessionDataComplete
};
